import { useT } from "@/src/i18n";
import {
  WALL_OBJECT_CATALOG,
  WALL_OBJECT_CATEGORIES,
  type WallObjectDefinition,
} from "@/src/core/engine/wallObjects";
import { useMapStore } from "@/src/store/useMapStore";

/**
 * Miniature vector preview of a wall object, drawn with the same conventions as
 * the map renderer so the picker reads as glyphs rather than a list of words.
 * SVG here rather than canvas: these are small, static, and want to scale with
 * the button.
 */
function WallGlyph({ definition }: { definition: WallObjectDefinition }) {
  const { style } = definition;
  const stroke = definition.dmOnly ? "#d08bce" : "currentColor";
  const parts = [];

  // Jambs — always present, so every glyph reads as an opening in a wall.
  parts.push(<path key="jambs" d="M4 4 L4 20 M28 4 L28 20" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" />);

  if (style.opening !== "full") {
    parts.push(<path key="sill" d="M4 12 L28 12" stroke={stroke} strokeWidth={style.leaf === "none" ? 1.6 : 2.6} strokeLinecap="round" />);
  }
  if (style.leaf === "single") {
    parts.push(<path key="leaf" d="M4 12 A 24 24 0 0 1 28 12" stroke={stroke} strokeWidth="1.1" fill="none" />);
  }
  if (style.leaf === "double") {
    parts.push(<path key="l1" d="M4 12 A 12 12 0 0 1 16 4" stroke={stroke} strokeWidth="1.1" fill="none" />);
    parts.push(<path key="l2" d="M28 12 A 12 12 0 0 0 16 4" stroke={stroke} strokeWidth="1.1" fill="none" />);
  }
  if (style.bars) {
    for (let index = 1; index <= style.bars; index += 1) {
      const x = 4 + (24 * index) / (style.bars + 1);
      parts.push(<path key={`bar${index}`} d={`M${x} 5 L${x} 19`} stroke={stroke} strokeWidth="1.3" />);
    }
  }
  if (style.wavy) {
    parts.push(<path key="wave" d="M4 12 q3 -6 6 0 t6 0 t6 0 t6 0" stroke={stroke} strokeWidth="1.3" fill="none" />);
  }
  if (style.barred) {
    parts.push(<path key="bar" d="M8 5 L24 19" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />);
  }
  if (style.lock) {
    parts.push(<circle key="lock" cx="16" cy="6" r="2" fill={stroke} />);
  }
  if (style.arrow) {
    parts.push(<path key="arrow" d="M16 5 L16 20 M13 17 L16 20 L19 17" stroke={stroke} strokeWidth="1.4" fill="none" strokeLinecap="round" />);
  }
  if (style.opening === "window") {
    parts.push(<rect key="win" x="9" y="8" width="14" height="8" stroke={stroke} strokeWidth="1.4" fill="none" />);
  }
  if (style.opening === "slit") {
    parts.push(<path key="slit" d="M16 6 L16 18" stroke={stroke} strokeWidth="2.4" strokeLinecap="round" />);
  }
  if (style.glyph) {
    parts.push(
      <text key="glyph" x="16" y="12" textAnchor="middle" dominantBaseline="central"
        fill={stroke} fontSize="11" fontWeight="700" fontFamily="Georgia, serif">
        {style.glyph}
      </text>,
    );
  }

  return (
    <svg viewBox="0 0 32 24" className="h-6 w-8" aria-hidden="true" fill="none">
      {parts}
    </svg>
  );
}

export function WallTypePicker() {
  const selected = useMapStore((state) => state.selectedWallType);
  const setSelected = useMapStore((state) => state.setSelectedWallType);
  const t = useT();

  return (
    <section className="border-t border-white/8 px-3 py-3">
      <p className="mb-1 px-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-600">
        {t("wall.pickerTitle")}
      </p>
      <p className="mb-2 px-1 text-[9px] leading-4 text-stone-600">{t("wall.pickerHint")}</p>

      {WALL_OBJECT_CATEGORIES.map((category) => {
        const entries = WALL_OBJECT_CATALOG.filter((entry) => entry.category === category);
        if (entries.length === 0) return null;
        return (
          <div key={category} className="mb-2 last:mb-0">
            <p className="mb-1 px-1 text-[8px] font-bold uppercase tracking-[0.16em] text-stone-700">
              {t(`wallCategory.${category}`)}
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {entries.map((entry) => {
                const active = selected === entry.id;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSelected(entry.id)}
                    aria-pressed={active}
                    title={`${t(entry.labelKey)}${entry.dmOnly ? ` · ${t("wall.dmOnly")}` : ""}`}
                    data-walltype={entry.id}
                    className={`relative flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl border p-1 text-center transition ${active ? "border-[#c3463d] bg-[#a8322c]/25 text-white" : "border-white/6 bg-white/[0.025] text-stone-400 hover:border-white/15 hover:bg-white/[0.06] hover:text-stone-100"}`}
                  >
                    <WallGlyph definition={entry} />
                    <span className="text-[8px] font-medium leading-none">{t(entry.labelKey)}</span>
                    {entry.dmOnly && (
                      <span
                        className="absolute end-1 top-1 h-1.5 w-1.5 rounded-full bg-[#d08bce]"
                        title={t("wall.dmOnlyHint")}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </section>
  );
}
